import { useLanguage } from "@/lib/i18n/language-provider";
import { GridCounter } from "./grid-counter";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import SmartLink from "@/components/smart-link";
import { Info, History, Download, Shield, FileText } from "lucide-react";

interface FooterProps {
  extraPadding?: boolean;
  showGridCounter?: boolean;
  showNavLinks?: boolean;
}

export function Footer({ extraPadding = false, showGridCounter = false, showNavLinks = false }: FooterProps) {
  const { t } = useLanguage();
  const pathname = usePathname();
  const [isEmbeddedInSynoptic, setIsEmbeddedInSynoptic] = useState(false);

  // Détecter automatiquement si nous sommes sur une page MultiGrid
  const isMultiGridPage = pathname?.includes('multi-grid') || pathname?.includes('multigrid');
  const shouldAddPadding = extraPadding || isMultiGridPage;

  useEffect(() => {
    // Vérifier si on est dans un iframe avec embed=synoptic
    const isInIframe = window.self !== window.top;
    const urlParams = new URLSearchParams(window.location.search);
    const embedParam = urlParams.get('embed');

    setIsEmbeddedInSynoptic(isInIframe && embedParam === 'synoptic');
  }, []);

  // Ne pas afficher le footer si on est dans l'iframe du Synoptique
  if (isEmbeddedInSynoptic) {
    return null;
  }

  return (
    <>
      {/* Espace supplémentaire avant le footer si nécessaire */}
      {shouldAddPadding && <div className="py-24"></div>}

      {/* Augmenter le z-index pour que le footer reste au-dessus du canvas */}
      <footer className="w-full py-4 border-t border-border/40 mt-auto relative z-50 bg-background">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center gap-3">
          {showGridCounter && <GridCounter />}
          
          {showNavLinks && (
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <SmartLink 
                href="/about/" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
              >
                <Info className="h-3.5 w-3.5" />
                {t("nav.about")}
              </SmartLink>
              <SmartLink 
                href="/changelogs/" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
              >
                <History className="h-3.5 w-3.5" />
                {t("nav.changelogs")}
              </SmartLink>
              <SmartLink 
                href="/downloads/" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                {t("nav.downloads")}
              </SmartLink>
              <SmartLink 
                href="/privacy/" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
              >
                <Shield className="h-3.5 w-3.5" />
                {t("nav.privacy")}
              </SmartLink>
              <SmartLink 
                href="/terms/" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
              >
                <FileText className="h-3.5 w-3.5" />
                {t("nav.terms")}
              </SmartLink>
            </div>
          )}
          
          <p className="text-sm text-muted-foreground">
            {t("footer.madeby") || "Made with ❤️ by Axel Pouppart"}
          </p>
        </div>
      </footer>
    </>
  );
} 